import React, { useState } from 'react';
import OutsideDetector from '../hooks/outsideDetector';
import DropdownMenu from './DropdownMenu';
import FormLoader from './FormLoader';
import useAlarm from '../context/AlarmContext';
import getUsersH2HReportData from '../helpers/getUsersH2HReportData';
import generateCVS from '../helpers/generateCVS';


const AdminExportUsers = ({ filters, searchQuery, hasNonUsers, checkedActiveUsers, checkedAllActiveUsers, totalUsers }) => {
  const [showLinkDropdown, setShowLinkDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setAlarm } = useAlarm();

  const getUsers = (status) => {
    const baseUrl = '/admin/users';
    const format = 'csv';
    const timestamp = new Date().getTime() / 1000;
    const setStatus = !status ? '' : status;
    const name = status
      ? `${status}-users-${
          new Date().getMonth() + 1
        }-${new Date().getDate()}-${new Date().getFullYear()}`
      : `all-users-${
          new Date().getMonth() + 1
        }-${new Date().getDate()}-${new Date().getFullYear()}`;
    setShowLinkDropdown(!showLinkDropdown);
    setIsLoading(true);

    fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
          .content,
      },
      body: JSON.stringify({
        format,
        t: timestamp,
        status: setStatus,
        filters,
        search: searchQuery,
      }),
    })
      .then((response) => {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/csv')) {
          return response.blob().then(blob => ({ isFile: true, data: blob }));
        } else if (contentType && contentType.includes('application/json')) {
          return response.json().then(json => ({ isFile: false, data: json }));
        } else {
          return response.json().then(json => ({ isFile: false, data: json }));
        }
      })
      .then((result) => {
        if (result.isFile) {
          const urlLink = window.URL.createObjectURL(new Blob([result.data]));
          const link = document.createElement('a');
          link.href = urlLink;
          link.setAttribute('download', `${name}.csv`);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(urlLink);
          
          setAlarm({ type: 'success', message: 'Export completed successfully!' });
        } else {
          setAlarm({ 
            type: 'success', 
            message: result.data.message || 'Export processing started. You will receive notifications about the progress.' 
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setAlarm({ type: 'error', message: 'Export failed. Please try again.' });
        setIsLoading(false);
      });
  };

  const getActivityReport = async () => {
    const baseUrl = '/api/v2/admin/users/users_details';
    const name = `activity-report-${
      new Date().getMonth() + 1
    }-${new Date().getDate()}-${new Date().getFullYear()}`;
    setShowLinkDropdown(!showLinkDropdown);
    setIsLoading(true);

    fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
          .content,
      },
      body: JSON.stringify({
        user_ids: checkedActiveUsers.map((user) => user.id),
        checkedAllUsers: checkedAllActiveUsers,
        filter: filters,
        search: searchQuery,
      }),
    })
      .then((response) => {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/csv')) {
          return response.blob().then(blob => ({ isFile: true, data: blob }));
        } else if (contentType && contentType.includes('application/json')) {
          return response.json().then(json => ({ isFile: false, data: json }));
        } else {
          return response.json().then(json => ({ isFile: false, data: json }));
        }
      })
      .then((result) => {
        if (result.isFile) {
          const urlLink = window.URL.createObjectURL(new Blob([result.data]));
          const link = document.createElement('a');
          link.href = urlLink;
          link.setAttribute('download', `${name}.csv`);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(urlLink);
          
          setAlarm({ type: 'success', message: 'Activity Report completed successfully!' });
        } else {
          setAlarm({ 
            type: 'success', 
            message: result.data.message || 'Activity Report processing started. You will receive notifications about the progress.' 
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setAlarm({ type: 'error', message: 'Activity Report failed. Please try again.' });
        setIsLoading(false);
      });
  };

  const handleH2HReport = async () => {
    setIsLoading(true);
    const habitData = await getUsersH2HReportData();

    if (habitData) {
      habitData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      let csv = 'Name, Email, Phone, Behavior, Module, Day, Created' + '\n';

      habitData.forEach((entry) => {
        csv += `"${entry.name}",${entry.email},${formatPhoneNumber(
          entry.phone
        )},"${entry.behavior_title}","${entry.module.title}",${
          entry.progress + 1
        },${formatDate(entry.created_at)}\n`;
      });

      const currentDate = new Date().toISOString().slice(0, 10);
      generateCVS(csv, `habits_report_${currentDate}`);
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return '---';
    }
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    };
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', options);
    return formattedDate.replace(/\//g, '-');
  };

  const formatPhoneNumber = (phoneNumber) => {
    const digits = phoneNumber.replace(/\D/g, '');

    if (digits.length !== 10) {
      return 'Invalid phone number';
    }

    const formattedNumber = `(${digits.slice(0, 3)}) ${digits.slice(
      3,
      6
    )}-${digits.slice(6)}`;

    return formattedNumber;
  };  

  const userExportActions = [
    {
      text: 'All Users',
      path: '',
      onClick: () => {
        getUsers();
      },
      subList: [],
      role: 'admin',
    },
    {
      text: 'Active Users',
      path: '',
      onClick: () => {
        getUsers('confirmed');
      },
      subList: [],
      role: 'admin',
    },
    {
      text: 'Unconfirmed Users',
      path: '',
      onClick: () => {
        getUsers('unconfirmed');
      },
      subList: [],
      role: 'admin',
    },
    {
      text: 'Pending Users',
      path: '',
      onClick: () => {
        getUsers('pending');
      },
      subList: [],
      role: 'admin',
    },
    {
      text: 'Help to Habit Report (Active Users)',
      path: '',
      onClick: () => {
        handleH2HReport();
      },
      subList: [],
      role: 'admin',
    },
  ];

  if (hasNonUsers) {
    userExportActions.push({
      text: 'Non Users',
      path: '',
      onClick: () => {
        getUsers('non');
      },
      subList: [],
      role: 'admin',
    });
  }

  if (checkedAllActiveUsers === true || checkedActiveUsers.length > 0) {
    userExportActions.push({
      text: `Activity Report for ${checkedAllActiveUsers ? totalUsers.toLocaleString('en-US') : checkedActiveUsers.length} Users`,
      path: '',
      onClick: () => {
        getActivityReport();
      },
      subList: [],
      role: 'admin',
    });
  }

  return (
    <OutsideDetector
      className="relative"
      stateSetter={setShowLinkDropdown}
      style={{ marginLeft: '0.5rem' }}
    >
      <button
        type="button"
        className="btn--inverse border flex p-4  font-semibold rounded-lg"
        onClick={() => setShowLinkDropdown(!showLinkDropdown)}
      >
        <span>Reports</span>
        <i className="mx-1 far fa-angle-down" />
      </button>
      {showLinkDropdown && (
        <DropdownMenu className="mt-3 pin-r" list={userExportActions} />
      )}
      {isLoading && <FormLoader />}
    </OutsideDetector>
  );
};

export default AdminExportUsers;
